// Supplier account — our account with a supplier, and the people we call.
//
// CEO 2026-09-10: "we need a place to store all of our vendor details —
// account numbers, contact phone and names." This is that place: on the
// supplier's own page (/admin/vendors/:id), where a rep building a PO looks.
// Passwords and API keys never appear here; they live in the credential files.
//
// Two ideas that look alike and are NOT the same (UX review, 2026-09-10):
//   · "Order inbox" is vendors.primary_email — the ONLY address PO Send uses.
//   · "Main contact" is a person in vendor_contacts. Marking someone main does
//     not route purchase orders. When the inbox is empty and the main contact
//     has an email, a one-click button copies it across so a rep is never
//     silently unrouted.
// Built from the brand components (Button / Input / StatusPill) and the house
// form grammar (UI review, 2026-09-10): labels are small caps, actions sit
// right-aligned with the error above them, removal is the inline two-step,
// never window.confirm.
import type React from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AccountVendor {
  id: string;
  name: string;
  primaryEmail: string | null;
  /** The art department's email — CC'd on POs (Stephanie 2026-10-16). */
  artEmail?: string | null;
  primaryPhone: string | null;
  website: string | null;
  notes: string | null;
  accountNumber: string | null;
  paymentTerms: string | null;
  portalUrl: string | null;
}
interface VendorContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  isPrimary: boolean;
}
interface AccountResponse {
  vendor: AccountVendor;
  contacts: VendorContact[];
}

type FieldKind = "text" | "url" | "email" | "textarea";
const ACCOUNT_FIELDS: Array<{ key: keyof AccountVendor; api: string; label: string; placeholder: string; kind: FieldKind }> = [
  // Rows (Stephanie 2026-10-16): Order email | Art email, then Payment terms
  // | Main phone — the grid is two across, so the order here is the layout.
  { key: "primaryEmail", api: "primary_email", label: "Order email", placeholder: "orders@supplier.com", kind: "email" },
  { key: "artEmail", api: "art_email", label: "Art email", placeholder: "art@supplier.com", kind: "email" },
  { key: "paymentTerms", api: "payment_terms", label: "Payment terms", placeholder: "e.g. Net 30, credit card", kind: "text" },
  { key: "primaryPhone", api: "primary_phone", label: "Main phone", placeholder: "800.555.0100", kind: "text" },
  { key: "accountNumber", api: "account_number", label: "Our account number", placeholder: "e.g. 44712", kind: "text" },
  { key: "portalUrl", api: "portal_url", label: "Ordering portal", placeholder: "https://", kind: "url" },
  { key: "notes", api: "notes", label: "Notes", placeholder: "Minimums, rush policy, who to call when a PO is late…", kind: "textarea" },
];

const ALWAYS_SHOWN = new Set<keyof AccountVendor>(["primaryEmail", "artEmail", "paymentTerms", "primaryPhone"]);

const BLANK_CONTACT = { name: "", role: "", email: "", phone: "", notes: "" };
type ContactDraft = typeof BLANK_CONTACT;

const LABEL = "mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted";
const TEXTAREA =
  "mt-1 flex min-h-[5rem] w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:cursor-not-allowed disabled:opacity-60";
const LINK = "text-xs font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-40 disabled:hover:text-ink";
const EYEBROW = "text-[10px] font-semibold uppercase tracking-wide text-brown-300";

/** What the server said, in words a rep can act on. */
function errText(e: unknown): string {
  const body = (e as { body?: { error?: string; message?: string } } | null)?.body;
  if (body?.message) return body.message;
  if (body?.error === "invalid body") return "Something in the form is not valid — check the email addresses and that the portal address starts with https://.";
  if (body?.error) return body.error;
  return e instanceof Error ? e.message : "Could not save. Try again.";
}

export function SupplierAccountSection(props: { vendorId: string; canEdit: boolean }): React.ReactElement {
  const qc = useQueryClient();
  // Same key the purchase-order page uses for this vendor: one cache, so an
  // inbox edited here is the inbox PO Send reads there.
  const { data, isLoading, error } = useQuery<AccountResponse>({
    queryKey: ["vendors", props.vendorId],
    queryFn: () => api(`/vendors/${props.vendorId}`),
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [contactDraft, setContactDraft] = useState<ContactDraft | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [errScope, setErrScope] = useState<"account" | "people">("account");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["vendors", props.vendorId] });
  const fail = (e: unknown) => { setErrScope("account"); setErr(errText(e)); };
  const failPeople = (e: unknown) => { setErrScope("people"); setErr(errText(e)); };
  const settle = () => { setErr(null); void invalidate(); };

  const saveAccount = useMutation({
    mutationFn: (body: Record<string, string | null>) => api(`/vendors/${props.vendorId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { setEditing(false); settle(); },
    onError: fail,
  });
  const saveContact = useMutation({
    mutationFn: (args: { id: string | null; body: Record<string, string | null> }) =>
      args.id
        ? api(`/vendors/${props.vendorId}/contacts/${args.id}`, { method: "PATCH", body: JSON.stringify(args.body) })
        : api(`/vendors/${props.vendorId}/contacts`, { method: "POST", body: JSON.stringify(args.body) }),
    onSuccess: () => { setContactDraft(null); setEditingContactId(null); settle(); },
    onError: failPeople,
  });
  const removeContact = useMutation({
    mutationFn: (id: string) => api(`/vendors/${props.vendorId}/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => { setConfirmingRemoveId(null); settle(); },
    onError: failPeople,
  });
  const setPrimary = useMutation({
    mutationFn: (id: string) => api(`/vendors/${props.vendorId}/contacts/${id}`, { method: "PATCH", body: JSON.stringify({ is_primary: true }) }),
    onSuccess: settle,
    onError: failPeople,
  });
  const useAsInbox = useMutation({
    mutationFn: (email: string) => api(`/vendors/${props.vendorId}`, { method: "PATCH", body: JSON.stringify({ primary_email: email }) }),
    onSuccess: settle,
    onError: fail,
  });

  if (isLoading) return <section className="rounded-md border border-brown-100 bg-white p-4 text-sm text-brown-300">Loading account…</section>;
  if (error || !data) return <section className="rounded-md border border-brown-100 bg-white p-4 text-sm text-rust-600">Could not load the supplier account.</section>;
  const { vendor, contacts } = data;
  const canEdit = props.canEdit;
  const busy = contactDraft !== null;
  const mainContact = contacts.find((c) => c.isPrimary) ?? null;
  // The paired rows always show (— when blank) so they stay side by side.
  const filled = ACCOUNT_FIELDS.filter((f) => ALWAYS_SHOWN.has(f.key) || (vendor[f.key] as string | null));

  const startEdit = () => {
    const d: Record<string, string> = {};
    for (const f of ACCOUNT_FIELDS) d[f.api] = (vendor[f.key] as string | null) ?? "";
    setDraft(d); setErr(null); setEditing(true);
  };
  const submitAccount = () => {
    const body: Record<string, string | null> = {};
    for (const f of ACCOUNT_FIELDS) {
      const before = (vendor[f.key] as string | null) ?? "";
      const after = draft[f.api] ?? "";
      if (after !== before) body[f.api] = after.trim() === "" ? null : after.trim();
    }
    if (Object.keys(body).length === 0) { setEditing(false); return; }
    saveAccount.mutate(body);
  };
  const openContact = (c: VendorContact | null) => {
    setErr(null);
    setEditingContactId(c?.id ?? null);
    setContactDraft(c ? { name: c.name, role: c.role ?? "", email: c.email ?? "", phone: c.phone ?? "", notes: c.notes ?? "" } : { ...BLANK_CONTACT });
  };
  const submitContact = () => {
    if (!contactDraft || contactDraft.name.trim() === "") return;
    const body: Record<string, string | null> = { name: contactDraft.name.trim() };
    for (const k of ["role", "email", "phone", "notes"] as const) body[k] = contactDraft[k].trim() === "" ? null : contactDraft[k].trim();
    saveContact.mutate({ id: editingContactId, body });
  };

  return (
    <section className="rounded-md border border-brown-100 bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <p className={EYEBROW}>Supplier account</p>
        {!editing && canEdit && (
          <button type="button" className={LINK} onClick={startEdit}>Edit</button>
        )}
      </div>

      {err && errScope === "account" && <p className="mb-3 text-sm text-rust-600">{err}</p>}

      {!vendor.primaryEmail && !editing && (
        <p className="mb-3 text-xs text-rust-600">
          No order inbox yet — a purchase order sent to this supplier has nowhere to go.
          {mainContact?.email && canEdit && (
            <>
              {" "}
              <button type="button" className="font-semibold underline hover:text-rust-600" disabled={useAsInbox.isPending} onClick={() => useAsInbox.mutate(mainContact.email as string)}>
                Use {mainContact.name}&rsquo;s email ({mainContact.email})
              </button>
            </>
          )}
        </p>
      )}

      {editing ? (
        <div>
          <div className="grid gap-3 sm:grid-cols-2">
            {ACCOUNT_FIELDS.map((f) => (
              <label key={f.api} className={cn("block", f.kind === "textarea" && "sm:col-span-2")}>
                <span className={LABEL}>{f.label}</span>
                {f.kind === "textarea" ? (
                  <textarea className={TEXTAREA} rows={3} value={draft[f.api] ?? ""} placeholder={f.placeholder} disabled={saveAccount.isPending} onChange={(e) => setDraft({ ...draft, [f.api]: e.target.value })} />
                ) : (
                  <Input type={f.kind === "text" ? "text" : f.kind} value={draft[f.api] ?? ""} placeholder={f.placeholder} disabled={saveAccount.isPending} onChange={(e) => setDraft({ ...draft, [f.api]: e.target.value })} />
                )}
                {f.api === "primary_email" && (
                  <span className="mt-1 block text-[11px] text-brown-300">The address a purchase order is emailed to. Marking a person &ldquo;Main contact&rdquo; below does not change it.</span>
                )}
                {f.api === "art_email" && (
                  <span className="mt-1 block text-[11px] text-brown-300">CC&rsquo;d on every PO to this supplier, so their art team gets the artwork. Saved as their &ldquo;Art department&rdquo; contact.</span>
                )}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saveAccount.isPending}>Cancel</Button>
            <Button type="button" size="sm" onClick={submitAccount} disabled={saveAccount.isPending}>{saveAccount.isPending ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      ) : filled.length === 0 ? (
        <p className="text-sm text-brown-300">
          Nothing recorded yet — account number, payment terms, order inbox, main phone, ordering portal, notes.
          {canEdit && (
            <>
              {" "}
              <button type="button" className="font-semibold text-ink hover:underline" onClick={startEdit}>Add them</button>.
            </>
          )}
        </p>
      ) : (
        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {filled.map((f) => {
            const v = (vendor[f.key] as string | null) ?? "";
            return (
              <div key={f.api} className={cn("flex items-baseline justify-between gap-3", f.kind === "textarea" && "sm:col-span-2")}>
                <dt className="shrink-0 text-brown-400">{f.label}</dt>
                <dd className={cn("text-right text-brown-500", f.kind === "textarea" && "whitespace-pre-wrap text-left")}>
                  {!v ? (
                    <span className="text-brown-300">—</span>
                  ) : f.kind === "url" ? (
                    <a href={v} target="_blank" rel="noreferrer" className="font-semibold text-ink hover:underline">{v.replace(/^https?:\/\//, "")}</a>
                  ) : f.kind === "email" ? (
                    <a href={`mailto:${v}`} className="hover:underline">{v}</a>
                  ) : (
                    v
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      <div className="mt-4 border-t border-brown-50 pt-3">
        <div className="mb-2 flex items-baseline justify-between">
          <p className={EYEBROW}>People</p>
          {!busy && canEdit && (
            <button type="button" className={LINK} onClick={() => openContact(null)}>Add person</button>
          )}
        </div>

        {err && errScope === "people" && <p className="mb-2 text-sm text-rust-600">{err}</p>}
        {contacts.length === 0 && !busy && (
          <p className="text-sm text-brown-300">No one recorded. Add your rep, accounts receivable, art or customer service.</p>
        )}

        {contacts.length > 0 && (
          <ul className="divide-y divide-brown-50 text-sm">
            {contacts.map((c) => (
              // Fixed two-row layout: people on the first line, note on the
              // second, actions always pinned right on the first line.
              <li key={c.id} className="py-2">
                <div className="flex items-baseline gap-3">
                  <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-semibold text-brown-600">{c.name}</span>
                    {c.isPrimary && <StatusPill tone="active">Main contact</StatusPill>}
                    {c.role && <span className="text-brown-400">{c.role}</span>}
                    {c.email && <a href={`mailto:${c.email}`} className="text-brown-500 hover:underline">{c.email}</a>}
                    {c.phone && <span className="text-brown-500">{c.phone}</span>}
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 items-baseline gap-2">
                      {confirmingRemoveId === c.id ? (
                        // The app's own two-step, not a browser dialog: removal
                        // is reversible (add the person again), so it must not
                        // spend a rep's attention on a native confirm.
                        <>
                          <span className="text-xs text-brown-400">Remove {c.name}?</span>
                          <button type="button" className="rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50" disabled={removeContact.isPending} onClick={() => removeContact.mutate(c.id)}>Remove</button>
                          <button type="button" className="rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100" onClick={() => setConfirmingRemoveId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {!c.isPrimary && (
                            <>
                              <button type="button" className={LINK} disabled={busy} onClick={() => setPrimary.mutate(c.id)}>Make main contact</button>
                              <span className="text-muted">·</span>
                            </>
                          )}
                          <button type="button" className={LINK} disabled={busy} onClick={() => openContact(c)}>Edit</button>
                          <span className="text-muted">·</span>
                          <button type="button" className={LINK} disabled={busy} onClick={() => setConfirmingRemoveId(c.id)}>Remove</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {c.notes && <p className="mt-0.5 text-xs text-brown-400">{c.notes}</p>}
              </li>
            ))}
          </ul>
        )}

        {contactDraft !== null && (
          <div className="mt-2 rounded-md border border-brown-100 bg-cream-50/40 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              {([
                ["name", "Name", "Cara Roworth"],
                ["role", "Role", "Sales rep, AR, Art, Customer service"],
                ["email", "Email", "name@supplier.com"],
                ["phone", "Phone", "800.555.0100 x12"],
              ] as const).map(([k, label, ph]) => (
                <label key={k} className="block">
                  <span className={LABEL}>{label}{k === "name" && <span className="text-rust-500"> *</span>}</span>
                  <Input type={k === "email" ? "email" : "text"} value={contactDraft[k]} placeholder={ph} disabled={saveContact.isPending} onChange={(e) => setContactDraft({ ...contactDraft, [k]: e.target.value })} />
                </label>
              ))}
              <label className="block sm:col-span-2">
                <span className={LABEL}>Notes</span>
                <Input type="text" value={contactDraft.notes} placeholder="Best for rush questions; out Fridays" disabled={saveContact.isPending} onChange={(e) => setContactDraft({ ...contactDraft, notes: e.target.value })} />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setContactDraft(null); setEditingContactId(null); }} disabled={saveContact.isPending}>Cancel</Button>
              <Button type="button" size="sm" onClick={submitContact} disabled={saveContact.isPending || contactDraft.name.trim() === ""}>{saveContact.isPending ? "Saving…" : editingContactId ? "Save" : "Add"}</Button>
            </div>
          </div>
        )}
      </div>
      <p className="mt-3 text-[11px] text-brown-300">Passwords and API keys are never stored here.</p>
    </section>
  );
}
